import React, { useCallback, useEffect, useState } from 'react';
import Swal from '../../theme/swal';
import {
  IcWifi, IcRefresh, IcCheckCircle, IcAlertTriangle, IcClock, IcBan, IcPrinter,
  IcUndo, IcSend, IcHourglass, IcCpu,
} from '../../theme/icons';
import { useEngine, engineApi, hasShell } from '../../desktop/engine';
import { apiClient } from '../../services/authService';
import KI from '../../theme/palette';

/*
 * « Ce poste » — page of the desktop app: state of the synchronisation with
 * the VPS (queue, conflicts, actions) and the peripherals plugged into this
 * PC. Reachable only when the app runs on the local engine.
 */

const STATUS_META = {
  pending: { label: 'En attente', cls: 'bdg-warn', Icon: IcClock },
  done: { label: 'Envoyée', cls: 'bdg-pos', Icon: IcCheckCircle },
  conflict: { label: 'Conflit', cls: 'bdg-alert', Icon: IcAlertTriangle },
  blocked: { label: 'Bloquée', cls: 'bdg-neutral', Icon: IcHourglass },
  abandoned: { label: 'Abandonnée', cls: 'bdg-off', Icon: IcBan },
};

const FILTERS = [
  { key: '', label: 'Tout' },
  { key: 'pending,blocked', label: 'En attente' },
  { key: 'conflict', label: 'Conflits' },
  { key: 'done', label: 'Envoyées' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function since(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} j`;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`bdg ${m.cls}`}>
      <m.Icon className="text-[1em]" /> {m.label}
    </span>
  );
}

function Stat({ label, value, tone = 'gray' }) {
  const tones = {
    gray: 'text-gray-900', amber: 'text-amber-700', red: 'text-red-600', green: 'text-green-700',
  };
  return (
    <div className="kpi rounded-lg px-4 py-3">
      <div className="kpi-label text-xs uppercase tracking-wide">{label}</div>
      <div className={`kpi-value text-2xl font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PostePage() {
  const { available, status } = useEngine();
  const [filter, setFilter] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      setEntries(await engineApi.outbox(filter));
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadEntries(); }, [loadEntries, status?.pending, status?.conflicts, status?.done]);

  const act = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      await loadEntries();
      if (okText) Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'success', title: okText });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Action impossible', text: e?.response?.data?.error || e.message, confirmButtonColor: KI.ink });
    } finally { setBusy(false); }
  };

  const abandon = (entry) => Swal.fire({
    title: 'Abandonner cette opération ?',
    text: `« ${entry.label} » ne sera jamais envoyée au serveur. Les données de ce poste seront réalignées sur le serveur.`,
    icon: 'warning', showCancelButton: true, confirmButtonText: 'Abandonner', cancelButtonText: 'Annuler',
    confirmButtonColor: KI.coralDark,
  }).then((r) => { if (r.isConfirmed) act(() => engineApi.abandon(entry.id), 'Opération abandonnée'); });

  if (available === false) {
    return (
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg p-8 text-center">
        <IcCpu className="mx-auto text-3xl text-gray-400 mb-3" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Ce poste</h1>
        <p className="text-gray-600">Cette page n'existe que dans l'application de bureau Ki Para Desktop.</p>
      </div>
    );
  }
  if (!status) {
    return <div className="text-gray-500 text-sm">Lecture de l'état du poste…</div>;
  }

  const online = status.upstream_online === true;
  const isDev = status.flavour === 'dev';
  let headline; let tone;
  if (!status.bootstrapped) {
    headline = status.has_token
      ? `Première synchronisation en cours… ${status.bootstrap_progress || ''}`
      : 'Première synchronisation : connectez-vous une fois avec Internet.';
    tone = 'amber';
  } else if (status.force_offline) {
    headline = 'Coupure simulée — le poste travaille en local, les opérations sont mises en file.';
    tone = 'red';
  } else if (online && status.serving === 'server') {
    headline = 'Connecté — les opérations vont directement au serveur, la copie locale se met à jour en arrière-plan';
    tone = 'green';
  } else if (online) {
    headline = `Connexion rétablie — envoi de ${status.pending} opération(s) au serveur, puis retour en direct`;
    tone = 'amber';
  } else {
    headline = `Hors ligne${status.offline_since ? ` depuis ${since(status.offline_since)}` : ''} — le poste travaille en local, ${status.pending} opération(s) à envoyer`;
    tone = 'red';
  }
  const toneCls = { green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-600' }[tone];

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Ce poste
            {isDev && <span className="bdg bdg-warn">DEV</span>}
          </h1>
          <p className="text-sm text-gray-500">
            {status.device_name || 'Poste'}{status.device_id ? ` · appareil n°${status.device_id}` : ''} · serveur {status.upstream}
            {status.version ? ` · version ${status.version}` : ''}
            {status.upstream_version && status.version && status.upstream_version !== status.version
              ? ` (serveur ${status.upstream_version})` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => act(engineApi.syncNow)} className="px-4 py-2 text-sm btn-s disabled:opacity-50">
            <IcRefresh className={status.syncing ? 'animate-spin' : ''} /> Synchroniser maintenant
          </button>
          <a href={engineApi.exportUrl()} className="px-4 py-2 text-sm btn-s" download>
            <IcSend /> Exporter la file
          </a>
          {isDev && (
            <button type="button" disabled={busy} onClick={() => act(() => engineApi.simulateOffline(!status.force_offline))} className="px-4 py-2 text-sm btn-g">
              <IcWifi /> {status.force_offline ? 'Rétablir la connexion' : 'Simuler une coupure'}
            </button>
          )}
        </div>
      </header>

      <section aria-labelledby="sync-title" className="space-y-4">
        <div role="status" aria-live="polite" className={`flex items-center gap-2 text-sm font-medium ${toneCls}`}>
          <IcWifi /> {headline}
          {status.syncing && <span className="text-gray-500 font-normal">· {status.phase === 'push' ? 'envoi' : status.phase === 'pull' ? 'réception' : status.phase === 'bootstrap' ? 'copie initiale' : 'synchronisation'}…</span>}
        </div>
        {status.last_error && <p className="text-sm text-red-600">{status.last_error}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="En attente" value={status.pending + status.blocked} tone={status.pending + status.blocked ? 'amber' : 'gray'} />
          <Stat label="Conflits" value={status.conflicts} tone={status.conflicts ? 'red' : 'gray'} />
          <Stat label="Envoyées" value={status.done} tone="green" />
          <Stat label="Dernière réception" value={status.last_pull ? fmtDate(status.last_pull) : '—'} />
        </div>
      </section>

      <section aria-labelledby="queue-title" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="queue-title" className="text-lg font-semibold text-gray-800">Opérations de ce poste</h2>
          <div className="flex gap-1 bg-gray-200 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                className={`px-3 py-1 text-sm rounded-md ${filter === f.key ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Chargement…</div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">Aucune opération dans cette liste.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="th-ki">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2">Quand</th>
                  <th className="px-4 py-2">Opération</th>
                  <th className="px-4 py-2">État</th>
                  <th className="px-4 py-2">Détail</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2 whitespace-nowrap tabular-nums text-gray-600">{fmtDate(e.captured_at)}</td>
                    <td className="px-4 py-2 text-gray-900">{e.label}<div className="text-xs text-gray-400">n°{e.id} · {e.method} {e.path.length > 48 ? `${e.path.slice(0, 48)}…` : e.path}</div></td>
                    <td className="px-4 py-2"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-2 text-gray-600 max-w-md">
                      {e.status === 'done' && e.sent_at ? `Envoyée le ${fmtDate(e.sent_at)}` : e.last_error || (e.attempts ? `${e.attempts} essai(s)` : '')}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {(e.status === 'conflict' || e.status === 'blocked') && (
                        <>
                          <button type="button" disabled={busy} onClick={() => act(() => engineApi.retry(e.id), 'Remise en file')} className="px-3 py-1 text-sm btn-s mr-2"><IcUndo /> Réessayer</button>
                          <button type="button" disabled={busy} onClick={() => abandon(e)} className="px-3 py-1 text-sm btn-db"><IcBan /> Abandonner</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {hasShell() && <PeripheralsSection />}

      <EngineLogSection />
    </div>
  );
}

/* ---------------------------------------------------------------- */
function EngineLogSection() {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState(null);
  const load = async () => {
    try {
      const res = await apiClient.get('sync/local/log/', { params: { lines: 200 } });
      setLog(res.data);
    } catch (e) {
      setLog({ path: '', lines: [`Journal illisible : ${e.message}`] });
    }
  };
  return (
    <section aria-labelledby="log-title" className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 id="log-title" className="text-lg font-semibold text-gray-800">Journal du moteur</h2>
        <button type="button" onClick={() => { setOpen(!open); if (!open) load(); }} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
          {open ? 'Masquer' : 'Afficher'}
        </button>
      </div>
      {open && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 text-xs text-gray-500">
            <span>{log?.path || '…'}</span>
            <button type="button" onClick={load} className="px-2 py-1 btn-g text-xs"><IcRefresh /> Rafraîchir</button>
          </div>
          <pre className="text-xs text-gray-700 p-4 overflow-x-auto max-h-96 whitespace-pre">
            {log ? log.lines.join('\n') : 'Chargement…'}
          </pre>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- */
function PeripheralsSection() {
  const [printers, setPrinters] = useState([]);
  const [ports, setPorts] = useState(null); // serial ports seen on this PC (null = not looked up yet)
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [list, current] = await Promise.all([window.kipara.listPrinters(), window.kipara.getPeripherals()]);
        setPrinters(list || []);
        setCfg(current || {});
      } catch { setCfg({}); }
    })();
  }, []);

  if (!cfg) return null;
  const update = (patch) => setCfg({ ...cfg, ...patch });
  const save = async () => {
    setSaving(true);
    try {
      await window.kipara.setPeripherals(cfg);
      Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success', title: 'Périphériques enregistrés' });
    } finally { setSaving(false); }
  };
  // Les tests (et la vente) utilisent la configuration ENREGISTRÉE côté
  // app : on enregistre d'abord ce qui est à l'écran pour tester sans piège.
  const test = async (fn, title) => {
    try {
      await window.kipara.setPeripherals(cfg);
      const r = await fn();
      if (r && r.ok === false) throw new Error(r.error || 'échec');
      Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success', title });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Test échoué', text: e.message });
    }
  };
  // Tiroir branché directement au PC : lister les ports série (COM) et
  // proposer le premier trouvé si aucun n'est encore saisi.
  const detectPorts = async () => {
    try {
      const list = typeof window.kipara.listSerialPorts === 'function' ? await window.kipara.listSerialPorts() : [];
      setPorts(list || []);
      if (list && list.length && !cfg.drawerCom) update({ drawerCom: list[0] });
      if (!list || !list.length) Swal.fire({ icon: 'info', title: 'Aucun port trouvé', text: "Branchez le tiroir (câble USB ou boîtier USB) puis réessayez. La mise à jour de l'app de bureau peut être nécessaire." });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Détection impossible', text: e.message });
    }
  };
  const PrinterSelect = ({ id, label, value, onChange }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select id={id} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white">
        <option value="">Imprimante par défaut de Windows</option>
        {printers.map((p) => <option key={p.name} value={p.name}>{p.displayName || p.name}{p.isDefault ? ' (défaut)' : ''}</option>)}
      </select>
    </div>
  );

  return (
    <section aria-labelledby="periph-title" className="w-full px-4 pt-2 pb-4 space-y-4">
      <h2 id="periph-title" className="text-lg font-semibold text-gray-800">Périphériques de ce poste</h2>
      <div className="bg-white border border-gray-200 rounded-lg p-5 grid gap-5 md:grid-cols-2">
        <PrinterSelect id="p-ticket" label="Imprimante ticket (reçus de vente)" value={cfg.ticketPrinter} onChange={(v) => update({ ticketPrinter: v })} />
        <PrinterSelect id="p-a4" label="Imprimante A4 (factures, bons de commande)" value={cfg.a4Printer} onChange={(v) => update({ a4Printer: v })} />
        <div>
          <label htmlFor="ticket-width" className="block text-sm font-medium text-gray-700 mb-1">Largeur du ticket</label>
          <select id="ticket-width" value={cfg.ticketWidth || '80'} onChange={(e) => update({ ticketWidth: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white">
            <option value="80">80 mm</option>
            <option value="58">58 mm</option>
          </select>
        </div>
        <div>
          <label htmlFor="drawer-mode" className="block text-sm font-medium text-gray-700 mb-1">Tiroir-caisse</label>
          <select id="drawer-mode" value={cfg.drawerMode || 'none'} onChange={(e) => update({ drawerMode: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white">
            <option value="none">Aucun</option>
            <option value="network">Via l'imprimante ticket réseau (port 9100)</option>
            <option value="share">Via l'imprimante ticket partagée Windows</option>
            <option value="com">Branché directement au PC (USB / port COM, sans imprimante)</option>
          </select>
          {cfg.drawerMode === 'network' && (
            <input aria-label="Adresse de l'imprimante réseau" value={cfg.drawerHost || ''} onChange={(e) => update({ drawerHost: e.target.value })} placeholder="192.168.1.50" className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          )}
          {cfg.drawerMode === 'share' && (
            <input aria-label="Nom de partage de l'imprimante" value={cfg.drawerShare || ''} onChange={(e) => update({ drawerShare: e.target.value })} placeholder="\\\\localhost\\TICKET" className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          )}
          {cfg.drawerMode === 'com' && (
            <div className="mt-2 flex gap-2">
              <input aria-label="Port du tiroir" list="drawer-ports" value={cfg.drawerCom || ''} onChange={(e) => update({ drawerCom: e.target.value })} placeholder="COM3" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
              <datalist id="drawer-ports">{(ports || []).map((p) => <option key={p} value={p} />)}</datalist>
              <button type="button" onClick={detectPorts} className="px-3 py-2 text-sm btn-s whitespace-nowrap">Détecter</button>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {cfg.drawerMode === 'com'
              ? "Le tiroir s'ouvre par une impulsion envoyée sur son port (COM3, COM4…). « Détecter » liste les ports vus par Windows."
              : "Le tiroir s'ouvre par une impulsion envoyée à l'imprimante ticket."}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
          <input type="checkbox" checked={!!cfg.openDrawerOnCash} onChange={(e) => update({ openDrawerOnCash: e.target.checked })} />
          Ouvrir le tiroir automatiquement après une vente en espèces
        </label>
        <div className="md:col-span-2 flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          <button type="button" disabled={saving} onClick={save} className="px-4 py-2 text-sm btn-p disabled:opacity-50">Enregistrer</button>
          <button type="button" onClick={() => test(() => window.kipara.printTest('ticket'), 'Test envoyé à l\'imprimante ticket')} className="px-4 py-2 text-sm btn-s"><IcPrinter /> Imprimer un test (ticket)</button>
          <button type="button" onClick={() => test(() => window.kipara.printTest('a4'), 'Test envoyé à l\'imprimante A4')} className="px-4 py-2 text-sm btn-s"><IcPrinter /> Imprimer un test (A4)</button>
          <button type="button" onClick={() => test(() => window.kipara.openDrawer(), 'Impulsion envoyée au tiroir')} className="px-4 py-2 text-sm btn-s">Tester le tiroir</button>
        </div>
      </div>
    </section>
  );
}
